import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { CreateProductOrderDto } from '@services/dtos'
import { getProductOrderSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid, Step, StepButton, Stepper } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import ProductOrderStepCart from './steps/ProductOrderStepCart'
import { FormHeader } from '../FormHeader'
import theme from '@utils/theme'
import ProductOrderStepInfo from './steps/ProductOrderStepInfo'
import { setSupplierFilters } from '@state/reducers/supplierReducer'
import { clearAddresses } from '@state/reducers/siteReducer'
import { createProductOrder, getProductOrdersToValidate, resetProductOrderFiles, setSelectedProducts } from '@state/reducers/productOrderReducer'
import { useSelector } from '@state/store'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import { setProductCatalogFilters } from '@state/reducers/productCatalogReducer'

export enum ProductOrderFormStep {
  Cart,
  Info
}

const steps: Record<ProductOrderFormStep, string> = {
  [ProductOrderFormStep.Cart]: 'Panier',
  [ProductOrderFormStep.Info]: 'Informations',
}

type Props = {
  onClose: () => void
}

const ProductOrderForm: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [step, setStep] = useState<ProductOrderFormStep>(ProductOrderFormStep.Cart)
  const [confirmSubmitDialogOpen, setConfirmSubmitDialogOpen] = useState(false)
  const { filesByProductId, selectedProducts } = useSelector(({ productOrder }) => {
    return { 
      filesByProductId: productOrder.filesByProductId, 
      selectedProducts: productOrder.selectedProducts 
    }
  })
  
  const useCreateForm = useForm<CreateProductOrderDto>({
    resolver: yupResolver(getProductOrderSchema(step)),
    context: { selectedProducts },
    mode: 'onSubmit',
    defaultValues: {
      lineItems: [],
    },
  })

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      useCreateForm.reset()
      dispatch(setSupplierFilters({ search: '', type: undefined }))
      dispatch(setProductCatalogFilters({ page: 0, search: undefined, supplierId: undefined, checkOrderExamAssociation: undefined }))
      dispatch(clearAddresses())
      dispatch(setSelectedProducts([]))
      dispatch(resetProductOrderFiles())
    }
  }, [dispatch])

  const lineItemsValues = useCreateForm.watch('lineItems') ?? []

  useEffect(() => {
    lineItemsValues.forEach((item, index) => {
      const hasFile = !!filesByProductId[item.productId]

      useCreateForm.setValue(
        `lineItems.${index}.hasGePurchaseOrderAttachment`,
        hasFile,
        { shouldValidate: true }
      )
    })
  }, [filesByProductId, lineItemsValues, useCreateForm.setValue])

  const onSubmitForm = async (data: CreateProductOrderDto) => {
    setIsSubmitting(true)
    const formData = new FormData()
    formData.append('payload', JSON.stringify({
      ...data,
      lineItems: data.lineItems.map(item => {
        const { isStandardDelay, hasGePurchaseOrderAttachment, ...rest } = item
        return rest
      })
    }))
    Object.entries(filesByProductId).forEach(([productId, file]) => {
      formData.append(`gePurchaseOrderAttachment_${productId}`, file)
    })
    dispatch(createProductOrder(formData as unknown as CreateProductOrderDto))
      .unwrap()
      .then(() => {
        cb('La commande a été passée avec succès')
        onClose()
        dispatch(getProductOrdersToValidate())
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const nextStep = async () => {
    const valid = await useCreateForm.trigger()
    if (!valid) {
      return
    }
    if (step < ProductOrderFormStep.Info) {
      setStep(prev => prev + 1)
      return
    }
    setConfirmSubmitDialogOpen(true)
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          useCreateForm.handleSubmit(onSubmitForm)(e)
        }}
        className="w-full h-full flex flex-col"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Commander des produits'}
          onSubmit={nextStep}
          submitText={step < ProductOrderFormStep.Info ? 'Continuer' : 'Commander'}
          centerElement={
            <Stepper activeStep={step} alternativeLabel className="gap-3">
              {Object.entries(steps).map(([key, label]) => {
                const stepIndex = Number(key) as ProductOrderFormStep
                const isActive = stepIndex === step
                const isPast = stepIndex < step
                return (
                  <Step key={label}>
                    <StepButton
                      disabled={!isPast}
                      onClick={() => isPast && setStep(stepIndex)}
                      icon={null}
                    >
                      <span style={{ color: isActive ? '#3A0CA1' : theme.palette.primary.main }}>
                        {label}
                      </span>
                    </StepButton>
                  </Step>
                )
              })}
            </Stepper>
          }
        />

        <Grid
          container
          rowSpacing={2}
          columnSpacing={4}
          className="flex-1"
          sx={{ height: '100%' }}
        >
          {step === ProductOrderFormStep.Cart && (
            <ProductOrderStepCart useCreateForm={useCreateForm} />
          )}
          {step === ProductOrderFormStep.Info && (
            <ProductOrderStepInfo useCreateForm={useCreateForm} />
          )}
        </Grid>
      </form>

      <ConfirmDialog
        open={confirmSubmitDialogOpen}
        title="Confirmer la commande"
        message={`Êtes-vous sûr de valider la commande ?${' '}
          ${selectedProducts.filter(({ validation }) => !!validation).length > 0 
            ? 'Elle passera en attente de validation et l\'email sera envoyé au fournisseur une fois approuvée' 
            : 'L\'email sera envoyé au fournisseur'}.`}
        onCancel={() => setConfirmSubmitDialogOpen(false)}
        onConfirm={() => {
          setConfirmSubmitDialogOpen(false)
          useCreateForm.handleSubmit(onSubmitForm)()
        }}
      />
    </div>
  )
}

export default ProductOrderForm
